/**
 * Connector pins and the jump pulse ring. A pin is page-relative (pinSize.ts: the connector's own
 * size, so it covers the same notes at any zoom and follows the page during a pinch) inside an
 * invisible ≥ 44 pt button kept on the page box; a tap goes to the nearest pin (hit areas overlap
 * where small pins do not). Its label and the pulse ring are counter-scaled during a pinch.
 */
import { CornerDownRight } from 'lucide-react';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { nearestPin, pinDiameterPx, pinLabelPlacement, pinLook } from './pinSize';
import type { ConnectorPin } from './types';

/** Things drawn ON the (always white) page use fixed colours that work in light and dark mode. */
export const PIN_COLOR = '#2a41a8';
export const PULSE_COLOR = '#e8590c';
/** Hit area of a pin (CSS px), whatever the size of the pin itself. */
const PIN_SIZE = 44;

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * The visible pin: a filled circle `d` px wide with the jump icon and a white ring. Also the
 * edit-mode markers (annotate/PageOverlay.tsx) and the Tengill size preview.
 */
export function PinMark({
  d,
  color,
  className,
  style,
  children,
}: {
  d: number;
  color: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  const look = pinLook(d);
  return (
    <span
      aria-hidden
      className={cx('flex shrink-0 items-center justify-center rounded-full text-white', className)}
      style={{ width: d, height: d, background: color, boxShadow: `0 0 0 ${look.ring}px #ffffff, 0 1px 3px rgb(0 0 0 / 0.35)`, ...style }}
    >
      <CornerDownRight aria-hidden size={look.icon} strokeWidth={look.icon < 16 ? 3 : 2.5} />
      {children}
    </span>
  );
}

export function ConnectorPinButton({
  pin,
  pagePins,
  boxW,
  boxH,
  inert,
  onActivate,
}: {
  pin: ConnectorPin;
  /** Every pin of the page (this one included): a tap activates the nearest. */
  pagePins: readonly ConnectorPin[];
  boxW: number;
  boxH: number;
  inert: boolean;
  onActivate: (pin: ConnectorPin) => void;
}) {
  const t = useT();
  const d = pinDiameterPx(pin.size, boxW);
  const look = pinLook(d);
  // The pin sits where it was placed; its hit area is centred on it but kept inside the page box.
  const half = PIN_SIZE / 2;
  const px = pin.x * boxW;
  const py = pin.y * boxH;
  const left = Math.min(Math.max(px, half), Math.max(half, boxW - half));
  const top = Math.min(Math.max(py, half), Math.max(half, boxH - half));
  const color = pin.color ?? PIN_COLOR;
  const labelLeft = pin.x > 0.7;
  const page = pin.target.page + 1;
  const label = pin.label ? t('viewer.jump_to_label', { label: pin.label, page }) : t('viewer.jump_to', { page });
  return (
    <button
      type="button"
      data-connector={pin.id}
      aria-label={label}
      title={label}
      tabIndex={inert ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        let hit = pin;
        // A tap / click (not Enter or Space) goes to the nearest pin — hit areas overlap where small
        // pins do not — except one on this pin's own label.
        const onLabel = e.target instanceof Element && e.target.closest('[data-pin-label]') !== null;
        const box = e.detail > 0 && !onLabel ? e.currentTarget.closest<HTMLElement>('[data-page]') : null;
        if (box) {
          // Measured on screen, so it holds during a pinch (the page box is scaled then).
          const r = box.getBoundingClientRect();
          hit = nearestPin(pagePins, e.clientX - r.left, e.clientY - r.top, r.width, r.height) ?? pin;
        }
        onActivate(hit);
      }}
      className="absolute z-10 rounded-full"
      style={{
        left,
        top,
        width: PIN_SIZE,
        height: PIN_SIZE,
        transform: 'translate(-50%, -50%)',
        pointerEvents: inert ? 'none' : 'auto',
        opacity: inert ? 0.55 : 1,
        touchAction: 'manipulation',
      }}
    >
      <PinMark
        d={d}
        color={color}
        className="absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: half + px - left, top: half + py - top }}
      >
        {pin.label ? (
          <span
            data-pin-label
            className="absolute top-1/2 -translate-y-1/2 rounded-md bg-white/95 leading-tight font-bold whitespace-nowrap shadow"
            // Counter-scaled during a pinch (its `transform`; the class's `translate` centres it).
            style={{ color, fontSize: look.font, padding: `${look.padY}px ${look.padX}px`, ...pinLabelPlacement(look, labelLeft) }}
          >
            {pin.label}
          </span>
        ) : null}
      </PinMark>
    </button>
  );
}

/** Three expanding rings at the jump target (a gentle fade with reduced motion), then removed. */
export function PulseRing({ x, y, onDone }: { x: number; y: number; onDone: () => void }) {
  const reduced = prefersReducedMotion();
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    const timer = setTimeout(() => doneRef.current(), reduced ? 2000 : 2800);
    return () => clearTimeout(timer);
  }, [reduced]);
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute z-20 rounded-full"
      onAnimationEnd={() => doneRef.current()}
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        width: 120,
        height: 120,
        border: `6px solid ${PULSE_COLOR}`,
        boxShadow: '0 0 0 3px rgb(255 255 255 / 0.85), inset 0 0 0 3px rgb(255 255 255 / 0.85)',
        transform: 'translate(-50%, -50%) scale(var(--kgr-pin-scale, 1))',
        opacity: 0,
        animation: reduced ? 'kgr-fade 1.6s ease-in-out 1' : 'kgr-pulse 0.8s ease-out 3',
      }}
    />
  );
}
